/**
 * AuthContext — Gère l'état de session utilisateur dans toute l'application.
 * Au montage : récupère le jeton CSRF, puis vérifie /auth/me/ pour une session existante.
 * Expose : user, loading, login, register, logout, refreshUser.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { initCsrf } from '../lib/api';

const AuthContext = createContext(null);

/**
 * Fournit l'état et les actions d'authentification à l'arbre de composants.
 * Au montage, initialise le CSRF puis vérifie l'existence d'une session.
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = chargement, false = non authentifié
  const [loading, setLoading] = useState(true);

  /** Récupère à nouveau l'utilisateur courant depuis /auth/me/ et met à jour l'état. */
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me/');
      if (res.data.authenticated) {
        setUser(res.data.user);
      } else {
        setUser(false);
      }
    } catch {
      setUser(false);
    }
  }, []);

  /** Authentifie avec les identifiants email/mot de passe. */
  const login = async (email, password) => {
    const res = await api.post('/auth/login/', { email, password });
    setUser(res.data);
    return res.data;
  };

  /** Crée un nouveau compte et connecte automatiquement l'utilisateur. */
  const register = async (firstName, lastName, email, password) => {
    const res = await api.post('/auth/register/', {
      first_name: firstName, last_name: lastName, email, password,
    });
    setUser(res.data);
    return res.data;
  };

  /** Met fin à la session courante et réinitialise l'état utilisateur. */
  const logout = async () => {
    try { await api.post('/auth/logout/'); } catch { /* session déjà expirée */ }
    setUser(false);
  };

  // Séquence de démarrage : jeton CSRF d'abord, puis vérification de session
  useEffect(() => {
    initCsrf()
      .catch(() => {})
      .then(() => refreshUser())
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook pour accéder à l'état et aux actions d'authentification (user, login, logout, etc.).
 * Doit être utilisé à l'intérieur d'un AuthProvider.
 * @returns {{ user: Object|false|null, loading: boolean, login: Function, register: Function, logout: Function, refreshUser: Function }}
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
